import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/services/api";
import {
  ArrowLeft,
  Wallet,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Award,
  Gift,
  Package,
  ArrowRight,
  RefreshCw,
  CreditCard,
  Banknote,
  AlertCircle,
  Calendar,
  ChevronRight,
} from "lucide-react";

const ESTADO_CONFIG = {
  pendiente: { label: "Pendiente", color: "bg-amber-100 text-amber-700", icon: Clock },
  disponible: { label: "Disponible", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  solicitado: { label: "Solicitado", color: "bg-blue-100 text-blue-700", icon: ArrowRight },
  pagado: { label: "Pagado", color: "bg-purple-100 text-purple-700", icon: Banknote },
};

const RETIRO_ESTADO_CONFIG = {
  pendiente: { label: "En revisión", color: "bg-amber-100 text-amber-700" },
  aprobado: { label: "Aprobado", color: "bg-blue-100 text-blue-700" },
  pagado: { label: "Pagado", color: "bg-green-100 text-green-700" },
  rechazado: { label: "Rechazado", color: "bg-red-100 text-red-700" },
};

export default function EmbajadorComisiones() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [comisiones, setComisiones] = useState([]);
  const [retiros, setRetiros] = useState([]);
  const [showRetiroDialog, setShowRetiroDialog] = useState(false);
  const [showDatosDialog, setShowDatosDialog] = useState(false);
  const [retiroMonto, setRetiroMonto] = useState("");
  const [processingRetiro, setProcessingRetiro] = useState(false);
  
  const [datosBancarios, setDatosBancarios] = useState({
    banco: "",
    tipo_cuenta: "ahorros",
    numero_cuenta: "",
    titular_cuenta: "",
  });

  const embajador = JSON.parse(localStorage.getItem("embajador") || "{}");

  const fetchData = useCallback(async () => {
    try {
      const [resumenRes, comisionesRes, retirosRes, perfilRes] = await Promise.all([
        api.get("/embajadores/comisiones/resumen"),
        api.get("/embajadores/comisiones/mis-comisiones?limit=20"),
        api.get("/embajadores/comisiones/mis-retiros"),
        api.get("/embajadores/me"),
      ]);
      
      setResumen(resumenRes.data);
      setComisiones(comisionesRes.data);
      setRetiros(retirosRes.data);
      
      // Update datos bancarios from profile
      const perfil = perfilRes.data;
      setDatosBancarios({
        banco: perfil.banco || "",
        tipo_cuenta: perfil.tipo_cuenta || "ahorros",
        numero_cuenta: perfil.numero_cuenta || "",
        titular_cuenta: perfil.titular_cuenta || "",
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        navigate("/embajador/login");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleSaveDatosBancarios = async () => {
    try {
      await api.put("/embajadores/me/perfil", datosBancarios);
      toast.success("Datos bancarios actualizados");
      setShowDatosDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Error al guardar datos");
    }
  };

  const handleSolicitarRetiro = async () => {
    const monto = parseFloat(retiroMonto);
    
    if (!monto || monto <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }
    
    if (monto > (resumen?.disponible || 0)) {
      toast.error("El monto excede tu saldo disponible");
      return;
    }
    
    if (monto < (resumen?.monto_minimo_retiro || 50000)) {
      toast.error(`El monto mínimo de retiro es $${(resumen?.monto_minimo_retiro || 50000).toLocaleString()}`);
      return;
    }
    
    if (!datosBancarios.banco || !datosBancarios.numero_cuenta) {
      toast.error("Debes configurar tus datos bancarios primero");
      setShowRetiroDialog(false);
      setShowDatosDialog(true);
      return;
    }
    
    setProcessingRetiro(true);
    try {
      await api.post("/embajadores/comisiones/solicitar-retiro", { monto });
      toast.success("¡Solicitud de retiro enviada!");
      setShowRetiroDialog(false);
      setRetiroMonto("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al solicitar retiro");
    } finally {
      setProcessingRetiro(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("es-CO", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const nivelNombre = {
    embajador: "Embajador",
    oro: "Embajador Oro",
    premium: "Embajador Premium",
    elite: "Embajador Elite",
  }[resumen?.nivel || "embajador"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate("/embajador/dashboard")}
              className="flex items-center gap-2 text-white/80 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
            <Button
              onClick={handleRefresh}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10"
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Wallet className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Mis Comisiones</h1>
              <p className="text-white/80 text-sm">
                {nivelNombre} • {resumen?.porcentaje_comision || 5}% de comisión
              </p>
            </div>
          </div>

          {/* Balance Cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-white/70 text-xs mb-1">Disponible para retirar</p>
              <p className="text-2xl font-bold">{formatCurrency(resumen?.disponible || 0)}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-white/70 text-xs mb-1">Pendiente de liberación</p>
              <p className="text-2xl font-bold">{formatCurrency(resumen?.pendiente || 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 -mt-4">
        {/* Action Cards */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            onClick={() => {
              if (!datosBancarios.banco || !datosBancarios.numero_cuenta) {
                setShowDatosDialog(true);
              } else if (resumen?.puede_retirar) {
                setRetiroMonto(String(resumen?.disponible || 0));
                setShowRetiroDialog(true);
              } else {
                toast.error(`Monto mínimo: ${formatCurrency(resumen?.monto_minimo_retiro || 50000)}`);
              }
            }}
            disabled={!resumen?.disponible}
            className={`bg-white rounded-xl p-4 shadow-md border text-left ${
              resumen?.puede_retirar 
                ? "border-green-200 hover:border-green-400" 
                : "border-gray-200 opacity-75"
            }`}
            data-testid="solicitar-retiro-btn"
          >
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center mb-2">
              <Banknote className="w-5 h-5 text-green-600" />
            </div>
            <p className="font-semibold text-gray-900">Solicitar Retiro</p>
            <p className="text-xs text-gray-500 mt-1">
              {resumen?.puede_retirar 
                ? "Tienes saldo disponible" 
                : `Mínimo ${formatCurrency(resumen?.monto_minimo_retiro || 50000)}`
              }
            </p>
          </button>

          <button
            onClick={() => setShowDatosDialog(true)}
            className="bg-white rounded-xl p-4 shadow-md border border-gray-200 hover:border-blue-200 text-left"
            data-testid="datos-bancarios-btn"
          >
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mb-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
            </div>
            <p className="font-semibold text-gray-900">Datos Bancarios</p>
            <p className="text-xs text-gray-500 mt-1">
              {datosBancarios.banco ? `${datosBancarios.banco}` : "No configurados"}
            </p>
          </button>
        </div>

        {/* Stats Summary */}
        <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-500" />
            Resumen Total
          </h2>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(resumen?.pendiente || 0)}</p>
              <p className="text-xs text-gray-500">En proceso</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{formatCurrency(resumen?.disponible || 0)}</p>
              <p className="text-xs text-gray-500">Disponible</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(resumen?.solicitado || 0)}</p>
              <p className="text-xs text-gray-500">Solicitado</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(resumen?.pagado || 0)}</p>
              <p className="text-xs text-gray-500">Pagado</p>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">Total histórico</span>
            <span className="text-lg font-bold text-gray-900">{formatCurrency(resumen?.total_historico || 0)}</span>
          </div>
        </div>

        {/* Recent Withdrawals */}
        {retiros.length > 0 && (
          <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100 mb-6">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-500" />
              Mis Retiros
            </h2>
            
            <div className="space-y-3">
              {retiros.slice(0, 5).map((retiro) => {
                const estadoConfig = RETIRO_ESTADO_CONFIG[retiro.estado] || RETIRO_ESTADO_CONFIG.pendiente;
                
                return (
                  <div key={retiro.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div>
                      <p className="font-semibold text-gray-900">{formatCurrency(retiro.monto)}</p>
                      <p className="text-xs text-gray-500">{formatDate(retiro.fecha_solicitud)}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${estadoConfig.color}`}>
                      {estadoConfig.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Commission History */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100">
          <div className="p-5 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-500" />
              Historial de Comisiones
            </h2>
          </div>
          
          {comisiones.length === 0 ? (
            <div className="text-center py-12">
              <Gift className="w-16 h-16 mx-auto mb-4 text-gray-200" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">Sin comisiones aún</h3>
              <p className="text-gray-500 text-sm">
                Comparte tu link para empezar a ganar
              </p>
              <Button
                onClick={() => navigate("/embajador/dashboard")}
                className="mt-4 bg-amber-500 hover:bg-amber-600"
              >
                Ir a compartir
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {comisiones.map((comision) => {
                const estadoConfig = ESTADO_CONFIG[comision.estado] || ESTADO_CONFIG.pendiente;
                const Icon = estadoConfig.icon;
                const isBono = comision.tipo === "bono";
                
                return (
                  <div key={comision.id} className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isBono ? "bg-purple-100" : "bg-amber-100"
                    }`}>
                      {isBono ? (
                        <Gift className="w-5 h-5 text-purple-600" />
                      ) : (
                        <Package className="w-5 h-5 text-amber-600" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900">
                        {isBono ? comision.bono_nombre : `Pedido #${comision.pedido_numero || comision.pedido_id?.slice(-6)}`}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        {formatDate(comision.fecha)}
                        {!isBono && (
                          <span className="text-gray-400">
                            • {comision.porcentaje_aplicado}% de {formatCurrency(comision.total_pedido)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className={`font-bold ${isBono ? "text-purple-600" : "text-amber-600"}`}>
                        +{formatCurrency(comision.monto)}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${estadoConfig.color}`}>
                        <Icon className="w-3 h-3" />
                        {estadoConfig.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Retiro Dialog */}
      <Dialog open={showRetiroDialog} onOpenChange={setShowRetiroDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-600" />
              Solicitar Retiro
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-green-50 rounded-xl p-4">
              <p className="text-sm text-green-800">Saldo disponible</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(resumen?.disponible || 0)}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Monto a retirar
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <Input
                  type="number"
                  value={retiroMonto}
                  onChange={(e) => setRetiroMonto(e.target.value)}
                  className="pl-8"
                  placeholder="0"
                  data-testid="retiro-monto-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Mínimo: {formatCurrency(resumen?.monto_minimo_retiro || 50000)}
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm text-gray-600 mb-2">Datos de pago:</p>
              <p className="font-medium text-gray-900">{datosBancarios.banco}</p>
              <p className="text-sm text-gray-600">
                {datosBancarios.tipo_cuenta === "ahorros" ? "Ahorros" : "Corriente"} •••• {datosBancarios.numero_cuenta?.slice(-4)}
              </p>
              <p className="text-sm text-gray-600">{datosBancarios.titular_cuenta}</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRetiroDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSolicitarRetiro}
              disabled={processingRetiro}
              className="bg-green-500 hover:bg-green-600"
              data-testid="retiro-submit-btn"
            >
              {processingRetiro ? "Procesando..." : "Solicitar Retiro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Datos Bancarios Dialog */}
      <Dialog open={showDatosDialog} onOpenChange={setShowDatosDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Datos Bancarios
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
              <Input
                value={datosBancarios.banco}
                onChange={(e) => setDatosBancarios({ ...datosBancarios, banco: e.target.value })}
                placeholder="Ej: Bancolombia, Davivienda..."
                data-testid="banco-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de cuenta</label>
              <select
                value={datosBancarios.tipo_cuenta}
                onChange={(e) => setDatosBancarios({ ...datosBancarios, tipo_cuenta: e.target.value })}
                className="w-full h-10 px-3 border border-gray-200 rounded-md"
              >
                <option value="ahorros">Ahorros</option>
                <option value="corriente">Corriente</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Número de cuenta</label>
              <Input
                value={datosBancarios.numero_cuenta}
                onChange={(e) => setDatosBancarios({ ...datosBancarios, numero_cuenta: e.target.value })}
                placeholder="Número de cuenta"
                data-testid="numero-cuenta-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titular de la cuenta</label>
              <Input
                value={datosBancarios.titular_cuenta}
                onChange={(e) => setDatosBancarios({ ...datosBancarios, titular_cuenta: e.target.value })}
                placeholder="Nombre del titular"
                data-testid="titular-cuenta-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDatosDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveDatosBancarios} className="bg-blue-500 hover:bg-blue-600">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
